import React, { useEffect, useState, useCallback } from 'react';
import { Checkbox, Row, Col, Input, notification, Radio } from 'antd';
import {
  ALL_EXCLUDED,
  COMPETITORS,
  PODIUM_TITLES,
  SEND_TO_CRM,
  AUTHORS,
  PODIUMAUTHORS,
  AUTHOR_TAGS,
  SOURCE,
  SENT_TO_CRM_RR
} from '../../../core/constants';
import { useQueryClient } from 'react-query';
import { useUpdateTitle } from '../../../core/modules/book/hooks/useUpdateTitle.query';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import { IBook } from '../../../section/Common/types/types';
import getTags from '../../../section/Common/utils/getTags';
import showNotification from '../../../section/Common/utils/showNotification';
import Modal from '../../../components/Modal';
import { useActions } from '../../../context/Actions.context';

const reasonForExclude = [
  'Author / Publisher',
  'Foreign Language',
  'Low Performer (manually)',
  'Rights Already Owned',
  'Subject Matter',
  'Audio already published'
];

interface IProps {
  book: IBook | any;
  modal: boolean;
  toggleModal: Function;
  id: string;
}

const excludeObject = (source, id, book) => {
  if (source === SOURCE.bookstat) {
    return { obj: { amazonasin: book.ASIN, title: book.Title }, obj_type: 'Title' };
  } else if (source === SOURCE['royal-road']) {
    return (id === AUTHORS || id === PODIUMAUTHORS) ?
      { obj: { author_id: book['Author Id'] }, obj_type: 'Author' } :
      { obj: { fiction_id: book['Fiction Id'], title: book.Title }, obj_type: 'Title' };
  }
};

const ExcludeTitles: React.FC<IProps> = ({ book, modal, toggleModal, id }) => {
  const { source, setActionLoading } = useActions();
  const exclude = excludeObject(source['source'], id, book);

  const { mutateAsync: mutateExclude } = useUpdateTitle(ALL_EXCLUDED);
  const { mutateAsync: mutatePodiumTitle } = useUpdateTitle(PODIUM_TITLES);
  const { mutateAsync: mutateCompetitors } = useUpdateTitle(COMPETITORS);
  const { mutateAsync: mutateExcludeAuthor } = useUpdateTitle(AUTHOR_TAGS);

  const queryClient = useQueryClient();

  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [input, setInput] = useState<string>('');
  const [isCheck, setIsCheck] = useState<boolean>(false);
  const [tags, setTags] = useState<CheckboxValueType[]>([]);
  const [selectionMode, setSelectionMode] = useState<'tags' | 'series'>('tags');
  const [notes, setNotes] = useState<string>('');

  const handleSelectionChange = (e) => {
    const val = e?.target?.value;
    setSelectionMode(val);

    if (val === 'series') {
      setTags([]);
      setIsCheck(false);
      setInput('');
      setButtonDisabled(false);
    }
    else
    {
      setButtonDisabled(true);
    }
  };

  const otherTabs = ({ tab, currentTags, mutateFN, body }) => {
    const index = currentTags.indexOf(tab);

    if (index !== -1) {
      currentTags.splice(index, 1);
      const params = exclude?.obj ?? {};
      const promise = mutateFN({ params, body });
      showNotification(
        promise,
        `The ${exclude?.obj_type} was successfully excluded.`,
        `The ${exclude?.obj_type} has not been excluded.`
      );
    }
  };

  const handleReset = () => {
    setTags([]);
    setInput('');
    setIsCheck(false);
    setButtonDisabled(true);
    setSelectionMode('tags');
    setNotes('');
  };

  const onSend = () => {
    setActionLoading(true);
    const currentTags = [...tags];

    const body = {
      ...(notes.trim() && { notes: notes.trim() })
    };

    otherTabs({ tab: 'Podium Title', currentTags, mutateFN: mutatePodiumTitle, body });
    otherTabs({ tab: 'Competitors', currentTags, mutateFN: mutateCompetitors, body });

    let tag = '';

    if (selectionMode === 'series') {
      tag = 'Exclude Entire Series';
    } else {
      tag = getTags(currentTags);

      if (isCheck) {
        tag += `&tag=${input}`;
      }
    }

    const params = exclude?.obj ? { ...exclude.obj } : {};
    const finalParams =
    selectionMode === 'series' && source['source'] === SOURCE.bookstat
      ? { ...params, seriesid: book?.seriesid, tag }
      : { ...params, tag };

    const promisePT =
    (id === AUTHORS || id === PODIUMAUTHORS)
      ? mutateExcludeAuthor({
        params: { ...params, tag: 'Excluded' },
        body
      })
      : mutateExclude({
        params: finalParams,
        body
      });

    showNotification(
      promisePT,
      `The ${exclude?.obj_type} was successfully excluded.`,
      `The ${exclude?.obj_type} has not been sent.`,
      book.Title
    );

    promisePT.then(() => {
      if (id === SEND_TO_CRM || id === SENT_TO_CRM_RR) {
        notification.info({
          description: 'Please delete the data from Hubspot.',
          message: ''
        });
      }
    });

    promisePT.then(() => queryClient.refetchQueries(`get-row-count${id}`));
    promisePT.then(() => setActionLoading(false));
    promisePT.then(() => queryClient.refetchQueries(`get-${id}`));
    toggleModal();
    handleReset();
  };

  const handleCheckboxGroup = useCallback(
    value => {
      setTags(value);

      if (value.length) {
        if (value.includes(undefined) && !input) {
          setButtonDisabled(true);
          return;
        } else {
          setButtonDisabled(false);
          return;
        }
      }
      setButtonDisabled(true);
    },
    [input]
  );

  const handleInput = useCallback(({ target }) => {
    setInput(target.value);
  }, []);

  useEffect(() => {
    if (input && isCheck && tags?.length) {
      setButtonDisabled(false);
    } else if (isCheck && !input) {
      setButtonDisabled(true);
    }
  }, [isCheck, input]);

  return (
    <Modal
      okText={`Exclude ${exclude?.obj_type}`}
      title={`Send ${exclude?.obj_type} to Exclusion List`}
      visible={modal}
      onOk={onSend}
      okButtonProps={{
        disabled: source['source'] === SOURCE['royal-road']
          ? false
          : buttonDisabled
      }}
      onCancel={() => {
        toggleModal();
        handleReset();
      }}
    >
      {
        source['source'] === SOURCE['royal-road'] ? (
          <p>{`Sending ${exclude?.obj_type} to Exclusion List. Please confirm.`}</p>
        ) : (
          <div style={{ marginBottom: 12 }}>
            <Radio.Group
              value={selectionMode}
              onChange={handleSelectionChange}
            >
              <Radio value='tags'>Select tag(s) for exclusion</Radio>

              <div style={{ marginTop: 12, marginLeft: 24 }}>
                <Checkbox.Group
                  className='modal-menu'
                  value={tags}
                  onChange={handleCheckboxGroup}
                  disabled={selectionMode === 'series'}
                >
                  <Row>
                    {reasonForExclude.map((reason) => (
                      <Col span={24} key={reason}>
                        <Checkbox value={reason}>{reason}</Checkbox>
                      </Col>
                    ))}

                    <Col span={24}>
                      <Checkbox
                        data-testid='input-checkbox'
                        checked={isCheck}
                        disabled={selectionMode === 'series'}
                        onChange={({ target }) => {
                          setIsCheck(target.checked);
                        }}
                      >
                        <Input
                          disabled={!isCheck || selectionMode === 'series'}
                          placeholder='Enter the tag'
                          className='modal-input'
                          onChange={handleInput}
                          value={input}
                        />
                      </Checkbox>
                    </Col>
                  </Row>
                </Checkbox.Group>
              </div>

              <div style={{ marginTop: 12 }}>
                <Radio value='series'>
                    Exclude an entire series
                  <div style={{ marginTop: 8, color: 'rgba(0,0,0,0.65)' }}>
                    <ul style={{ paddingLeft: 18 }}>
                      <li>
                          When selected, the title will be tagged as Exclude Entire Series.
                      </li>
                      <li>
                          Future titles from this series will be automatically sent to the Exclusion List.
                      </li>
                      <li>
                          Other titles from the author that are not part of this series will not be sent
                          to the Exclusion List.
                      </li>
                    </ul>
                  </div>
                </Radio>
              </div>
            </Radio.Group>
          </div>
        )
      }

      <Input.TextArea
        placeholder='Leave a note stating the reason (optional)'
        maxLength={400}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={4}
      />

      <div style={{ textAlign: 'right', fontSize: 12, marginTop: 4 }}>
        {notes.length} / 400
      </div>
    </Modal>
  );
};

export default React.memo(ExcludeTitles);
